import { type CreateImportJobInput, type ImportJob, importKeys } from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { retrying } from '@/app/retrying'
import { useApi } from '@/app/api'
import { problemDetail } from '../internal/problem.ts'

export interface UseImportJobsOptions {
  readonly ledgerId: () => string | undefined
  readonly enabled: () => boolean
}

export interface UseImportJobs {
  readonly jobs: ComputedRef<readonly ImportJob[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly isCreating: ComputedRef<boolean>
  readonly createErrorDetail: ComputedRef<string | undefined>
  retry(): void
  create(input: CreateImportJobInput): Promise<ImportJob>
}

export function useImportJobs(options: UseImportJobsOptions): UseImportJobs {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const listQuery = useQuery({
    queryKey: computed(() => importKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.imports.list(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  function invalidate(): Promise<void> {
    const ledgerId = options.ledgerId()
    if (ledgerId === undefined) return Promise.resolve()
    return Promise.all(
      importKeys.invalidatedByWrite(ledgerId).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ).then(() => undefined)
  }

  const createMutation = useMutation({
    mutationFn: (input: CreateImportJobInput) => api.imports.create(options.ledgerId()!, input),
    onSuccess: () => invalidate(),
  })

  return {
    jobs: computed(() => listQuery.data.value ?? []),
    isLoading: computed(() => active.value && listQuery.isPending.value),
    error: computed(() => listQuery.error.value),
    retrying: retrying(listQuery),
    isCreating: computed(() => createMutation.isPending.value),
    createErrorDetail: computed(() => problemDetail(createMutation.error.value)),
    retry: () => {
      void listQuery.refetch()
    },
    create: (input) => createMutation.mutateAsync(input),
  }
}
